import cx from "classnames";
import { isEmpty } from "lodash";
import { FC, useEffect, useMemo, useState } from "react";

import { RcFile } from "antd/es/upload";
import { LoadingOutlined } from "@ant-design/icons";
import { Button, Spin, Table, message } from "antd";
import Image, { StaticImageData } from "next/image";
import { DocumentViewer } from "react-documents";
import { HiCloudDownload, HiEye } from "react-icons/hi";

import ExcelViewer from "../ExcelViewer";
import { HiOutlineArrowTopRightOnSquare, HiXMark } from "react-icons/hi2";
import getRandomPlaceholder from "@/assets/placeholder";
import { useGetFileURL } from "@/hooks/useUploadFile";
import { useGetImageOrientation } from "@/utils/helper";
import { extractColors } from "extract-colors";
import * as XLSX from "xlsx";
import { RxSpeakerOff } from "react-icons/rx";

import dynamic from "next/dynamic";
import useWindowResize from "@/hooks/useWindowResize";

const PDFViewer = dynamic(() => import("../PDFViewer"), {
  ssr: false,
});

export type TImages = {
  src: string | StaticImageData | null;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  staticImg?: boolean;
  quality?: number;
  originalFile?: RcFile;
  setOrientation?: (orientation: "landscape" | "portrait" | "square") => void;
  setPrimaryColor?: (hex: string) => void;
  imageForPreview?: boolean;
  containerClassName?: string;
};

function isDocs(url: string): {
  isDocument: boolean;
  fileType: string | null;
  isExcel: boolean;
  isWord: boolean;
  isPdf: boolean;
  isVideo?: boolean;
} {
  try {
    let filename
    if (typeof url === "string") {
      filename = url.split("/").pop()?.split("?")[0]; // Remove query params
    }

    if (!filename)
      return {
        isDocument: false,
        fileType: null,
        isExcel: false,
        isWord: false,
        isPdf: false,
        isVideo: false,
      };

    const extension = filename?.split(".")?.pop()?.toLowerCase();

    const mimeTypes: { [key: string]: string } = {
      pdf: "application/pdf",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      xls: "application/vnd.ms-excel",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ppt: "application/vnd.ms-powerpoint",
      pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      csv: "text/csv",
      txt: "text/plain",
      html: "text/html",
      md: "text/markdown",
      rtf: "application/rtf",
    };

    const mediaFormats = new Set([
      "jpg",
      "jpeg",
      "png",
      "gif",
      "bmp",
      "svg",
      "tiff",
      "ico",
      "webp",
      "raw", // Image
    ]);
    const videoExtensions = new Set([
      "mp4",
      "avi",
      "mov",
      "wmv",
      "flv",
      "mkv",
      "webm",
      "mpg",
      "mpeg",
      "3gp",
      "m4v",
    ]);

    const fileType =
      extension && mimeTypes[extension] ? mimeTypes[extension] : null;

    const isExcel = extension === "xls" || extension === "xlsx";
    const isWord = extension === "doc" || extension === "docx";
    const isPdf = extension === "pdf";
    const isVideo = videoExtensions.has(extension ?? "");
    const isDocument =
      !!fileType && !mediaFormats.has(extension ?? "") && !isVideo; // Exclude media formats and video

    return { isDocument, fileType, isExcel, isWord, isPdf, isVideo };
  } catch (error) {
    console.log(error)
    console.log(`Error checking document format: ${error}`);
    return {
      isDocument: false,
      fileType: null,
      isExcel: false,
      isWord: false,
      isPdf: false,
      isVideo: false,
    };
  }
}

const _ExcelViewer: FC<{ url: string }> = ({ url }) => {
  const [excelData, setExcelData] = useState<any[]>([]);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [activeSheet, setActiveSheet] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadExcelFile = async () => {
      try {
        setLoading(true);
        const response = await fetch(url);
        const arrayBuffer = await response.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });

        const sheets = workbook.SheetNames;
        setSheetNames(sheets);
        setActiveSheet(sheets[0]);

        const worksheet = workbook.Sheets[sheets[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        setExcelData(jsonData);
      } catch (error) {
        console.error("Error loading Excel file:", error);
        message.error("Failed to load Excel file");
      } finally {
        setLoading(false);
      }
    };

    loadExcelFile();
  }, [url]);

  const handleSheetChange = (sheetName: string) => {
    try {
      setLoading(true);
      fetch(url)
        .then((response) => response.arrayBuffer())
        .then((arrayBuffer) => {
          const workbook = XLSX.read(arrayBuffer, { type: "array" });
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          setExcelData(jsonData);
          setActiveSheet(sheetName);
        })
        .finally(() => setLoading(false));
    } catch (error) {
      console.error("Error switching sheet:", error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spin size="large" />
      </div>
    );
  }

  const [headers, ...rows] = excelData;
  const columns =
    headers?.map((header: any, index: number) => ({
      title: header || `Column ${index + 1}`,
      dataIndex: index,
      key: index,
      render: (text: any) => text?.toString() || "",
    })) || [];

  const tableData =
    rows?.map((row: any[], index: number) => ({
      key: index,
      ...row,
    })) || [];

  return (
    <div className="w-full">
      <div className="overflow-auto max-h-[600px]">
        <Table
          columns={columns}
          dataSource={tableData}
          pagination={false}
          scroll={{
            x: true,
            y: 500,
          }}
          size="small"
          sticky={{
            offsetHeader: 0,
          }}
          className="excel-table"
          style={
            {
              "--ant-table-header-bg": "#f5f5f5",
              "--ant-table-header-color": "#262626",
            } as React.CSSProperties
          }
        />
      </div>
      <div className="flex justify-between items-center mb-4">
        <Button icon={<HiCloudDownload />} type="primary" href={url} download>
          Download
        </Button>
        <div className="flex gap-2">
          {sheetNames.map((sheetName) => (
            <Button
              key={sheetName}
              type={activeSheet === sheetName ? "primary" : "default"}
              size="small"
              onClick={() => handleSheetChange(sheetName)}
            >
              {sheetName}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};

const WordViewer: FC<{ url: string }> = ({ url }) => {
  const [htmlContent, setHtmlContent] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [viewerError, setViewerError] = useState(false);

  useEffect(() => {
    const loadWordFile = async () => {
      try {
        setLoading(true);
        setViewerError(false);

        // Use Google Docs Viewer
        const googleViewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true`;

        setHtmlContent(`
          <iframe 
            src="${googleViewerUrl}" 
            width="100%" 
            height="600px" 
            frameborder="0"
            onError="this.style.display='none'; this.nextSibling.style.display='block';"
          ></iframe>
          <div style="display:none;" class="text-center p-8">
            <p class="mb-4">Google Docs Viewer could not load this document.</p>
            <p class="text-sm text-gray-600">This may happen with private files or unsupported formats.</p>
          </div>
        `);
      } catch (error) {
        console.error("Error loading Word file:", error);
        setViewerError(true);
        setHtmlContent(`
          <div class="text-center p-8">
            <p class="mb-4">Preview not available for this Word document.</p>
            <p class="text-sm text-gray-600">Please download the file to view its contents.</p>
          </div>
        `);
      } finally {
        setLoading(false);
      }
    };

    loadWordFile();
  }, [url]);

  const handleIframeError = () => {
    setViewerError(true);
    setHtmlContent(`
      <div class="text-center p-8">
        <p class="mb-4">Google Docs Viewer could not load this document.</p>
        <p class="text-sm text-gray-600">This may happen with private files. Please download to view.</p>
      </div>
    `);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="w-full">
      {viewerError ? (
        <div className="p-8 text-center bg-gray-50">
          <p className="mb-4">Document preview is not available.</p>
          <p className="text-sm text-gray-600 mb-4">
            This can happen with private files or certain document formats.
          </p>
          <div className="flex gap-2 justify-center">
            <Button
              icon={<HiEye />}
              onClick={() =>
                window.open(
                  `https://docs.google.com/gview?url=${encodeURIComponent(url)}`,
                  "_blank",
                )
              }
            >
              Try in New Tab
            </Button>
            <Button
              icon={<HiCloudDownload />}
              type="primary"
              href={url}
              download
            >
              Download
            </Button>
          </div>
        </div>
      ) : (
        <div
          className="overflow-hidden"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      )}
      <div className="flex justify-between items-center mb-4">
        <Button icon={<HiCloudDownload />} type="primary" href={url} download>
          Download File
        </Button>
      </div>
    </div>
  );
};

const Images: FC<TImages> = ({
  src,
  alt,
  width,
  height,
  className,
  staticImg,
  quality,
  setOrientation,
  setPrimaryColor,
  originalFile,
  imageForPreview = false,
  containerClassName
}) => {
  const {
    isLoading: isLoadingImg,
    url: urlImg,
    fileSize,
  } = useGetFileURL(src as string, true);
  const [isLoadingLoadImg, setLoadingLoadImg] = useState(true);
  const [openImageViewer, setOpenImageViewer] = useState(false);
  const [isVideoMuted, isVideoMutedSet] = useState(true);

  useEffect(() => {
    if (urlImg) {
      extractColors(urlImg).then((res) => {
        if (res?.[0]?.hex && setPrimaryColor) {
          setPrimaryColor(res?.[res?.length - 1]?.hex);
        }
      });
    }
  }, [urlImg]);

  const srcImg = useMemo(() => {
    if (isLoadingImg) return "";
    if (staticImg) return src || getRandomPlaceholder();
    if (!src) return getRandomPlaceholder();
    if (isEmpty(urlImg)) return getRandomPlaceholder();
    return urlImg;
  }, [isLoadingImg, urlImg, src, staticImg]);

  const docMetadata = originalFile
    ? isDocs(originalFile.name)
    : isDocs(srcImg as string);
  const { orientation } = useGetImageOrientation(srcImg as string);

  useEffect(() => {
    setLoadingLoadImg(true);
  }, [src]);

  useEffect(() => {
    setOrientation && setOrientation(orientation);
  }, [orientation]);

  // Excel file viewer
  const excelViewer = useMemo(
    () =>
      srcImg &&
      docMetadata.isExcel && (
        <div className="relative w-full overflow-x-auto p-2">
          <ExcelViewer noHeader url={srcImg as string} />
        </div>
      ),
    [srcImg, docMetadata.isExcel],
  );

  // Word document viewer
  const wordViewer = useMemo(
    () =>
      srcImg &&
      docMetadata.isWord && (
        <div className="relative">
          <WordViewer url={srcImg as string} />
        </div>
      ),
    [srcImg, docMetadata.isWord],
  );

  const isMobile = useWindowResize();

  // Other documents viewer (PDF, etc.) - using react-documents
  const documentViewer = useMemo(
    () =>
      srcImg &&
      docMetadata.isDocument &&
      !docMetadata.isExcel &&
      !docMetadata.isWord && (
        <div className="bg-[#1A1A1A] h-full flex items-center flex-col relative w-full p-2">
          {docMetadata?.isPdf ? (
            fileSize && fileSize > 19 && isMobile ? (
              <div className="flex-1 flex justify-center items-center py-4">
                <p className="text-center text-neutral-300">
                  This file is too large to display here. <br />Please use a desktop
                  device or download the file <br />to view its contents.
                </p>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <PDFViewer src={srcImg as string} />
              </div>
            )
          ) : (
            <DocumentViewer
              className="w-full"
              style={{ minHeight: 500, minWidth: "100%" }}
              url={srcImg as string}
              viewer={docMetadata.isPdf && originalFile ? "pdf" : "google"}
              googleCheckContentLoaded
              googleCheckInterval={10000}
            />
          )}
          <div className="w-full flex items-start self-end">
            <Button
              className="mt-2"
              icon={<HiCloudDownload />}
              type="primary"
              href={srcImg as string}
              download
            >
              Download File
            </Button>
          </div>
        </div>
      ),
    [srcImg, isLoadingImg, src, docMetadata],
  );

  const videoViewer = useMemo(() => {
    let srcList = [
      { src: "/video.mp4", type: "video/mp4" },
      { src: "/video.webm", type: "video/webm" },
      { src: "/video.ogv", type: "video/ogg" },
      // Not recommended, but technically you could try:
      { src: "/video.mov", type: "video/quicktime" },
    ];
    if (!srcImg || !docMetadata.isVideo) return null;

    return (
      <div className="w-full aspect-[5/2] bg-black overflow-hidden relative group">
        <video
          src={srcImg as string}
          controls
          controlsList="nodownload"
          preload="metadata"
          autoPlay
          muted={isVideoMuted}
          onClick={() => isVideoMutedSet(false)}
          loop
          onVolumeChange={(e) => {
            const videoEl = e.currentTarget as HTMLVideoElement;
            isVideoMutedSet(videoEl.muted);
          }}
          className="absolute inset-0 w-full h-full object-contain"
        >
          {srcList.map(({ src, type }) => (
            <source key={type} src={src} type={type} />
          ))}
          Your browser does not support the video tag.
        </video>
        {isVideoMuted && (
          <div className="opacity-0 z-[5] absolute left-0 right-0 top-0 pt-2 group-hover:opacity-100 duration-1000 flex justify-center">
            <div
              onClick={() => isVideoMutedSet(false)}
              role="button"
              className="cursor-pointer bg-gray-400/50 p-2 text-xs rounded-lg hover:bg-gray-400/30"
            >
              Unmute <RxSpeakerOff className="ml-1" />
            </div>
          </div>
        )}
      </div>
    );
  }, [srcImg, docMetadata.isVideo, isVideoMuted]);

  // Handle different document types
  if (docMetadata.isDocument) {
    if (docMetadata.isExcel) {
      return excelViewer;
    }
    if (docMetadata.isWord) {
      return wordViewer;
    }
    return documentViewer;
  } else if (docMetadata.isVideo) {
    return videoViewer;
  }

  // Regular image display
  return (
    <div className="w-full h-full relative flex justify-center">
      {(isLoadingImg || isLoadingLoadImg) && srcImg && (
        <div
          className={cx(
            className,
            "flex items-center justify-center w-full h-full bg-neutral-800 min-h-[100px] absolute top-0 bottom-0 left-0 right-0",
          )}
        >
          <div className="flex flex-col gap-3 text-center">
            <Spin
              indicator={
                <LoadingOutlined
                  spin
                  width={80}
                  height={80}
                  color="#158284"
                  className="text-[#158284]"
                />
              }
              size="large"
            />
            <p className="text-neutral-300 text-sm">Loading...</p>
          </div>
        </div>
      )}
      {srcImg && (
        <div className={cx(containerClassName)}>
          <Image
            className={cx(className, "z-0")}
            src={srcImg}
            alt={alt}
            width={width}
            height={height}
            onLoadingComplete={() => setLoadingLoadImg(false)}
            quality={quality}
          />
          {imageForPreview && !isLoadingLoadImg && (
            <div
              onClick={() => setOpenImageViewer(true)}
              className="cursor-pointer group absolute top-0 right-0 m-4 p-0.5 bg-[rgba(0,0,0,.75)] w-10 h-10"
            >
              <div className="group-hover:rounded-full p-1 hover:bg-[rgba(220, 220, 220, 0.75)] transition-all duration-300 ease-in-out">
                <HiOutlineArrowTopRightOnSquare className="w-full h-full p-1" />
              </div>
            </div>
          )}
        </div>
      )}
      {openImageViewer && (
        <div className="fixed top-0 inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-80">
          <div className="relative">
            <Image
              src={srcImg}
              alt={alt}
              width={width || 800}
              height={height || 600}
              quality={quality}
              className="rounded shadow-lg max-w-[90vw] max-h-[90vh] object-contain z-100"
            />
            <button
              onClick={() => setOpenImageViewer(false)}
              className="absolute h-8 w-8 flex items-center justify-center top-2 right-2 text-black bg-white bg-opacity-80 rounded-full p-2 hover:bg-opacity-100 transition"
              aria-label="Close"
            >
              <HiXMark className="w-full h-full" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Images;
